import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from "../../services/api";
import { download, ExportButton, fileName, rangeLabel, tableSheet } from "./excel";

const money = (value) => (value === null || value === undefined ? "-" : Number(value).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const qty = (value) => (value === null || value === undefined ? "-" : Number(value).toLocaleString("en-IN", { maximumFractionDigits: 3 }));

const VIEWS = {
  customer: {
    label: "Customer Wise",
    endpoint: "/api/v1/reports/receivables",
    dateFilter: false,
    rowKey: (row) => row.customerId,
    columns: [
      { key: "customerName", label: "Customer" },
      { key: "totalInvoiced", label: "Total Invoiced", format: "money" },
      { key: "totalCollected", label: "Total Collected", format: "money" },
      { key: "outstanding", label: "Outstanding", format: "money" },
    ],
  },
  bill: {
    label: "Bill Wise",
    endpoint: "/api/v1/reports/outstanding-bills/bill-wise",
    dateFilter: true,
    rowKey: (row) => row.invoiceId,
    columns: [
      { key: "invoiceNumber", label: "Invoice #" },
      { key: "invoiceDate", label: "Date" },
      { key: "customerName", label: "Customer" },
      { key: "status", label: "Status" },
      { key: "billAmount", label: "Bill Amount", format: "money" },
      { key: "amountPaid", label: "Amount Paid", format: "money" },
      { key: "balanceDue", label: "Balance Due", format: "money" },
      { key: "ageingDays", label: "Ageing (Days)" },
    ],
  },
  route: {
    label: "Route Wise",
    endpoint: "/api/v1/reports/outstanding-bills/route-wise",
    dateFilter: true,
    rowKey: (row) => row.beatId ?? "unassigned",
    columns: [
      { key: "beatName", label: "Route / Beat" },
      { key: "billCount", label: "Bills" },
      { key: "totalOutstanding", label: "Total Outstanding", format: "money" },
    ],
  },
  salesman: {
    label: "Salesman Wise",
    endpoint: "/api/v1/reports/outstanding-bills/salesman-wise",
    dateFilter: true,
    rowKey: (row) => row.salesPersonId ?? "unassigned",
    columns: [
      { key: "salesPersonName", label: "Salesman" },
      { key: "billCount", label: "Bills" },
      { key: "totalOutstanding", label: "Total Outstanding", format: "money" },
    ],
  },
  company: {
    label: "Company Wise",
    endpoint: "/api/v1/reports/outstanding-bills/company-wise",
    dateFilter: true,
    superAdminOnly: true,
    rowKey: (row) => row.distributorId,
    columns: [
      { key: "companyName", label: "Company (Distributor)" },
      { key: "billCount", label: "Bills" },
      { key: "totalOutstanding", label: "Total Outstanding", format: "money" },
    ],
  },
};

const formatCell = (value, column) => {
  if (value === null || value === undefined || value === "") return "-";
  if (column.format === "money") return money(value);
  if (column.format === "qty") return qty(value);
  return String(value);
};

const OutstandingBillsReport = () => {
  const [view, setView] = useState("customer");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // the view and dates the rows on screen were loaded with — the export follows these, not half-edited filters
  const [applied, setApplied] = useState(null);

  const config = VIEWS[view];

  const exportExcel = () => {
    const loadedView = VIEWS[applied.view];
    const title = `Outstanding Bills — ${loadedView.label}`;
    const filters = loadedView.dateFilter ? rangeLabel(applied.fromDate, applied.toDate) : "Current position";
    download(fileName(`Outstanding Bills ${loadedView.label}`, loadedView.dateFilter ? filters : ""), [
      tableSheet(title, filters, loadedView.columns, rows, loadedView.label),
    ]);
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = config.dateFilter ? { fromDate: fromDate || undefined, toDate: toDate || undefined } : undefined;
      const data = await api.get(config.endpoint, params);
      setRows(Array.isArray(data) ? data : []);
      setApplied({ view, fromDate, toDate });
    } catch (error) {
      toast.error(error.message || "Failed to load report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const totals = config.columns.reduce((acc, column) => {
    if (column.format === "money" || column.format === "qty") {
      acc[column.key] = rows.reduce((sum, row) => sum + (Number(row[column.key]) || 0), 0);
    }
    return acc;
  }, {});

  return (
    <div className="p-4" style={{ position: "relative" }}>
      {loading ? (
        <div className="loading-overlay">
          <div className="spinner-card">
            <div className="spinner-ring" />
            <div className="spinner-label">Loading report...</div>
          </div>
        </div>
      ) : null}

      <div className="page-subtitle" style={{ marginBottom: "12px" }}>
        Unpaid / partially-paid sales invoices, grouped however you need to chase them down. Fully paid and
        cancelled invoices are excluded.
        {config.superAdminOnly ? " Company Wise rolls up outstanding across every distributor company — Super Admin only." : ""}
      </div>

      <div className="filters-bar">
        <select value={view} onChange={(e) => setView(e.target.value)} aria-label="Group by">
          {Object.entries(VIEWS).map(([key, v]) => (
            <option key={key} value={key}>
              {v.label}
            </option>
          ))}
        </select>
        {config.dateFilter ? (
          <>
            <input type="date" onClick={(e) => e.target.showPicker?.()} value={fromDate} onChange={(e) => setFromDate(e.target.value)} aria-label="From date" />
            <input type="date" onClick={(e) => e.target.showPicker?.()} value={toDate} onChange={(e) => setToDate(e.target.value)} aria-label="To date" />
          </>
        ) : null}
        <button className="primary" onClick={load} disabled={loading}>
          {loading ? "Loading..." : "Run Report"}
        </button>
        <ExportButton onClick={exportExcel} disabled={loading || rows.length === 0 || applied?.view !== view} />
      </div>

      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              {config.columns.map((column) => (
                <th key={column.key} style={{ textAlign: "center" }}>
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={config.columns.length}>
                  <div className="empty-state">
                    <span className="empty-state-title">No outstanding bills</span>
                  </div>
                </td>
              </tr>
            ) : (
              rows.map((row, index) => (
                <tr key={config.rowKey(row, index)}>
                  {config.columns.map((column) => (
                    <td key={column.key} style={{ textAlign: "center" }}>
                      {formatCell(row[column.key], column)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
          {rows.length > 0 ? (
            <tfoot>
              <tr>
                {config.columns.map((column, index) => (
                  <td key={column.key} style={{ fontWeight: 700, textAlign: "center" }}>
                    {index === 0 ? "Total" : totals[column.key] !== undefined ? formatCell(totals[column.key], column) : ""}
                  </td>
                ))}
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>
    </div>
  );
};

export default OutstandingBillsReport;
